import { ReactNode, useEffect, useMemo, useRef } from 'react'
import { Box, Button, Card, CardContent, Typography } from '@mui/material'
import { motion } from 'framer-motion'
import Link from 'next/link'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import twemoji from '@twemoji/api'
import { PartnerPageData } from 'types/types'
import { Game } from 'hooks/usePartnerGames'
import { isStarSportsPage } from '@/utils/starSportsUtils'
import { getGamePrizes } from '@/utils/prizeTiers'
import SportIcon from './SportIcon'
import CurrentUserPosition from './CurrentUserPosition'
import GameCountdown from './GameCountdown'
import PrizeJackpotSection from './PrizeJackpotSection'

export const ROUND_STATUS = {
  ACTIVE: 'ACTIVE',
  UPCOMING: 'UPCOMING',
  FINISHED: 'FINISHED',
} as const

export const GameTitle = ({ title }: { title: string }) => {
  const ref = useRef(null)

  useEffect(() => {
    if (ref.current) {
      twemoji.parse(ref.current, { folder: 'svg', ext: '.svg' })
    }
  }, [title])

  return <h2 ref={ref}>{title}</h2>
}

export type RegularGameItem = Game & {
  kind: 'game'
  is_user_joined: boolean
  next_round_is_user_joined?: boolean
}
export type RoundGameItem = Game & {
  kind: 'round'
  is_user_joined: boolean
  next_round_is_user_joined?: boolean
  roundId: number
  roundName: string | undefined
  roundStatus: string
  roundStartDate: string
  roundIndex: number
}
export type ExpandedGame = RegularGameItem | RoundGameItem

export function GameCard({
  game,
  index,
  page,
  primaryColor,
  secondaryColor,
  isAuthenticated,
  isLoadingJoinStatus,
  getGameHref,
  extraContent,
}: {
  game: ExpandedGame
  index: number
  page: PartnerPageData
  primaryColor: string
  secondaryColor: string
  isAuthenticated: boolean
  isLoadingJoinStatus: boolean
  getGameHref?: (game: ExpandedGame) => string
  extraContent?: ReactNode
}) {
  const textColorDark = '#353535'
  const textColorLight = '#64645B'
  const bgColor = '#e4e9ef'
  const textColor = '#9CA3AF'
  const liveStatusColor = '#EF4444'
  const currentPosition = '#f0fdf4'
  const borderPosition = '#bbf7d0'
  // Check if this is a round tile from a multi-round game
  const isRoundTile = game.kind === 'round'

  const singleRoundId = !isRoundTile && game.rounds?.length === 1 ? game.rounds[0].id : undefined

  // For round tiles, determine status from round status; for regular games, use game status
  const displayStatus: string = isRoundTile ? game.roundStatus : game.status
  const displayRoundName = isRoundTile ? game.roundName : game.next_round_name
  const hasUpcomingRound = isRoundTile
    ? displayStatus === ROUND_STATUS.UPCOMING
    : game.has_multiple_rounds && !!game.next_round_start_time

  const isLive = isRoundTile ? displayStatus === 'ACTIVE' : game.is_in_progress && !hasUpcomingRound
  const isFinished = displayStatus === 'FINISHED'
  const isEntered = isAuthenticated && !!game.is_user_joined
  const isHorseRacing = game.sport_name?.toLowerCase() === 'horse racing'

  let buttonText = 'Play Now'
  if (isFinished) {
    buttonText = 'View Game'
  } else if (isLive) {
    buttonText = isEntered ? 'View your Predictions' : 'View Game'
  } else if (isEntered) {
    buttonText = 'View your Predictions'
  }

  const state = useMemo(() => {
    if (isFinished) {
      if (isEntered) return 'ENTERED'
      return game.has_multiple_rounds ? 'MISSED' : 'CLOSED'
    }
    if (isLive) return isEntered ? 'ENTERED' : 'CLOSED'
    if (isEntered) return 'ENTERED'
    return 'OPEN'
  }, [isEntered, isFinished, isLive, game.has_multiple_rounds])

  const chipStyles = {
    ENTERED: { bg: '#ECFDF5', text: '#10B981', border: '#A7F3D0', label: 'Entered' },
    MISSED: { bg: '#fffbeb', text: '#d97706', border: '#f59e0b', label: 'Missed Round' },
    CLOSED: { bg: '#f3f4f6', text: '#6B7280', border: '#D1D5DB', label: 'Closed' },
    OPEN: { bg: '#EFF6FF', text: '#3B82F6', border: '#BFDBFE', label: 'Open' },
  }[state]

  const buttonLink = useMemo(() => {
    if (getGameHref) return getGameHref(game)
    if (page.feature_flags.is_star_sports_page && !isAuthenticated) {
      const query = isRoundTile
        ? `?gameId=${game.id}&roundId=${game.roundId}&flowType=JOIN`
        : `?gameId=${game.id}&flowType=JOIN`
      return `/${page.company_domain}/auth/external-login${query}&redirectTo=/${page.company_domain}/games/${game.id}`
    }
    return isRoundTile
      ? `/${page.company_domain}/games/${game.id}?roundId=${game.roundId}`
      : `/${page.company_domain}/games/${game.id}`
  }, [isRoundTile, page, game, getGameHref, isAuthenticated])

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index, 6) * 0.04 }}
      style={{ width: '100%', height: '100%', display: 'flex' }}
    >
      <Card
        sx={{
          width: '100%',
          borderRadius: '12px',
          minHeight: '160px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'white',
        }}
      >
        <CardContent
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            flexGrow: 1,
            height: '100%',
            position: 'relative',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Box
              position="absolute"
              top="16px"
              left="16px"
              display="flex"
              alignItems="center"
              gap="6px"
            >
              {isLive && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap="5px"
                  px={1.25}
                  py={0.4}
                  borderRadius="999px"
                  fontSize="0.75rem"
                  fontWeight={700}
                  bgcolor="#FEE2E2"
                  color={liveStatusColor}
                  border="1px solid #FECACA"
                  sx={{ letterSpacing: '0.05em' }}
                >
                  <Box
                    width="7px"
                    height="7px"
                    borderRadius="50%"
                    bgcolor={liveStatusColor}
                    sx={{
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.4 },
                      },
                    }}
                  />
                  LIVE
                </Box>
              )}
              {isAuthenticated &&
                (isLoadingJoinStatus ? (
                  <Box
                    width="64px"
                    height="24px"
                    borderRadius="999px"
                    bgcolor="grey.200"
                    sx={{
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.4 },
                      },
                    }}
                  />
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="6px"
                    px={1.25}
                    py={0.4}
                    borderRadius="999px"
                    fontSize="0.75rem"
                    fontWeight={600}
                    bgcolor={chipStyles.bg}
                    color={chipStyles.text}
                    border="1px solid"
                    borderColor={chipStyles.border}
                    sx={{ letterSpacing: '0.02em' }}
                  >
                    {chipStyles.label}
                  </Box>
                ))}
            </Box>

            <Box
              position="absolute"
              top="16px"
              right="16px"
              display="flex"
              alignItems="flex-start"
              gap="8px"
            >
              {page.feature_flags.show_players_entered !== false &&
                game.participant_count != null && (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="4px"
                    px={1.25}
                    py={0.4}
                    borderRadius="999px"
                    fontSize="0.75rem"
                    fontWeight={700}
                    bgcolor="grey.100"
                    color={textColorDark}
                    border="1px solid"
                    borderColor="grey.300"
                    flexShrink={0}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <GroupOutlinedIcon sx={{ fontSize: '0.95rem', color: textColor }} />
                    {game.participant_count.toLocaleString('en-GB')}
                  </Box>
                )}
              {game.tournament_image ? (
                <Box
                  component="img"
                  src={game.tournament_image}
                  alt="tournament"
                  width="58px"
                  height="58px"
                  sx={{ objectFit: 'contain', borderRadius: '4px', flexShrink: 0 }}
                />
              ) : (
                <Box
                  fontSize="1.25rem"
                  width="42px"
                  height="42px"
                  bgcolor="grey.100"
                  borderRadius="8px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <SportIcon
                    sportName={game.sport_name}
                    sx={{ fontSize: '1.7rem', color: textColor }}
                  />
                </Box>
              )}
            </Box>
            {/* Title */}
            <Typography
              component="div"
              sx={{
                fontSize: '0.815rem',
                fontWeight: 800,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: textColorDark,
                fontFamily: page.fonts?.main_font?.family,
                marginTop: 6.5,
                '& img.emoji': {
                  height: '1.7rem',
                  width: '1.7rem',
                  verticalAlign: 'middle',
                  margin: '0',
                },
              }}
            >
              <GameTitle title={game.title} />
            </Typography>

            {game.has_multiple_rounds &&
              (isRoundTile || hasUpcomingRound || isFinished) &&
              displayRoundName && (
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    fontStyle: 'bold',
                    fontWeight: 600,
                    color: textColorDark,
                    fontFamily: page.fonts?.secondary_font?.family,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {displayRoundName}
                </Typography>
              )}

            {/* Description */}
            {(game.status === 'ACTIVE' || game.status === 'UPCOMING') && (
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: textColorLight,
                  fontFamily: page.fonts?.secondary_font?.family,
                }}
              >
                {game.description}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(isRoundTile ? displayStatus === 'ACTIVE' : game.status === 'ACTIVE') && isEntered && (
              <CurrentUserPosition
                gameId={game.id}
                roundId={
                  isRoundTile
                    ? game.roundId
                    : game.rounds?.find((round) => round.status === 'ACTIVE')?.id
                }
                enableRoundToggle={!!game.has_multiple_rounds}
                isUserJoined={!!game.is_user_joined}
                gamePosition={game.position}
                gameScore={game.score}
                gameWinnerName={game.winner_name}
                gameWinnerScore={game.winner_score}
                isHorseRacing={isHorseRacing}
                pageTheme={page}
                label="Current Position"
                textColor="#15803d"
                sx={{
                  background: currentPosition,
                  border: `1px solid ${borderPosition}`,
                }}
              />
            )}
            {/* Starts in */}
            {!isFinished && (
              <GameCountdown
                startTime={
                  isRoundTile
                    ? game.roundStartDate
                    : (game.has_multiple_rounds && game.next_round_start_time) || game.deadline
                }
                isInProgress={isLive}
                status={displayStatus ?? game.status}
                label={hasUpcomingRound && !isFinished ? 'Round starts in: ' : undefined}
              />
            )}
            {(isRoundTile
              ? displayStatus === 'UPCOMING' || displayStatus === 'ACTIVE'
              : game.status === 'UPCOMING' || game.status === 'ACTIVE') && (
              <PrizeJackpotSection
                jackpot={game.jackpot}
                prizes={getGamePrizes(game)}
                fallbackPrize={game.prize || page.prize_header || '—'}
                sx={{
                  background: '#FFF8E5',
                  border: '1px solid #FFDF86',
                  borderRadius: '6px',
                  color: textColorLight,
                  px: 2,
                  py: 1.25,
                }}
              />
            )}

            {isAuthenticated &&
              (isRoundTile ? displayStatus === 'FINISHED' : game.status === 'FINISHED') &&
              !isEntered && (
                <Box
                  borderRadius="0.375rem"
                  p="0.125rem"
                  sx={{ background: bgColor, border: `1px solid ${textColor}` }}
                >
                  <CurrentUserPosition
                    gameId={game.id}
                    {...(isRoundTile
                      ? { roundId: game.roundId }
                      : singleRoundId != null && { winnerRoundId: singleRoundId })}
                    isUserJoined={false}
                    showWinner
                    missedRound={isFinished && !!game.has_multiple_rounds}
                    gamePosition={game.position}
                    gameScore={game.score}
                    gameWinnerName={game.winner_name}
                    gameWinnerScore={game.winner_score}
                    isHorseRacing={isHorseRacing}
                    pageTheme={page}
                    label="Your Position"
                    textColor="#6B7280"
                    sx={{ background: bgColor, border: 'none' }}
                  />
                </Box>
              )}

            {isAuthenticated &&
              (isRoundTile ? displayStatus === 'FINISHED' : game.status === 'FINISHED') &&
              isEntered && (
                <Box
                  borderRadius="0.375rem"
                  p="0.125rem"
                  sx={{ background: bgColor, border: `1px solid ${textColor}` }}
                >
                  <CurrentUserPosition
                    gameId={game.id}
                    {...(isRoundTile
                      ? { roundId: game.roundId }
                      : singleRoundId != null && { winnerRoundId: singleRoundId })}
                    isUserJoined={true}
                    showTotalPoints
                    showWinner
                    isFinished
                    gamePosition={game.position}
                    gameScore={game.score}
                    gameWinnerName={game.winner_name}
                    gameWinnerScore={game.winner_score}
                    isHorseRacing={isHorseRacing}
                    pageTheme={page}
                    label="Your Position"
                    textColor="#6B7280"
                    sx={{ background: bgColor, border: 'none' }}
                  />
                </Box>
              )}

            {extraContent}

            {/* CTA */}
            <Link href={buttonLink} prefetch={false} style={{ textDecoration: 'none' }}>
              <Button
                fullWidth
                endIcon={<ArrowRightAltIcon />}
                sx={{
                  height: 44,
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  bgcolor: primaryColor,
                  color: '#000',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: secondaryColor,
                  },
                }}
              >
                {buttonText}
              </Button>
            </Link>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  )
}
